import {
  Component,
  createRef,
  lazy,
  useContext,
  useEffect,
  useState,
} from "react";

import "@aws-amplify/ui/dist/style.css";
import { Button, styled, useMediaQuery, useTheme } from "@mui/material";
import * as Sentry from "@sentry/react";
import Amplify from "aws-amplify";
import { Authenticator, Greetings, Loading, SignIn } from "aws-amplify-react";
import { AxiosResponse } from "axios";
import { MaterialDesignContent, SnackbarProvider } from "notistack";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { PageTitleProvider, usePageTitle } from "./PageTitle";
import { useDisplayAlias } from "../hub/hubLabel";

import { axios } from "../api";
import AppLayout from "../components/AppBackground";
import SectionHomeLayout from "../components/sectionDashboard/SectionHomeLayout";
import CustomLoading, { hideLoader } from "../components/AppLoading";
import { FeatureContext } from "./Features";
import CustomSignIn from "./SignIn";

import type { AeronetV6Response } from "../types";
import type { IAeronetNav, IAeronetNavItem } from "./types";
import { applyNavModeChildren } from "./navModeRegistry";
import GcipGate from "../auth/GcipGate";
import WakeGate from "../components/WakeGate";
import { StepUpHost } from "../components/StepUpDialog";
import { getSessionUser } from "../auth/session";
import { getAuthProvider, getCognitoConfig, getProduct, isPureV6Tenant } from "../config/product";
import {
  filterNavForTenant,
  MOBILE_EMULATOR_FUNCTION,
  mobileEmulatorTarget,
  sanitizeMaintenanceNav,
} from "./navVisibility";
import { signOut as gcipSignOut } from "../auth/gcip";
import { takeReturnTo } from "../auth/authRecovery";
import Invoices from "../invoices/Invoices";
import Components from "../component/Component";
import Inspections from "../inspections/Inspections";
import Reports from "../reports/Reports";
import DueDate from "../DueDate/DueDate";
import { LoadingView } from "../components/Loading";
import { useCurrentUser } from "../hooks/useCurrentUser";
import MetaBaseDashboard from "../dashboard/metabase/MetabaseDashbord";
import MetabaseRoutes from "../routes/MetabaseRoutes";
import { navRequestConfig } from "./navRequest";
import { recordSignIn } from "./signInWrites";


const Dashboard = lazy(() => import("../dashboard/Dashboard"));
const InspectionsTR = lazy(() => import("../inspections-tr"));
const NotFound = lazy(() => import("./NotFound"));
const Settings = lazy(() => import("./Settings"));
const Tools = lazy(() => import("../tools/Tools"));
const AircraftServices = lazy(() => import("../aircraft-services"));
const ToolRequests = lazy(() => import("../tool-requests"));
const Tasks = lazy(() => import("../tasks"));
const Sors = lazy(() => import("../sors/Sors"));
const Rmas = lazy(() => import("../rmas/Rmas"));
const Aircraft = lazy(() => import("../aircraft/Aircraft"));
const Fleet = lazy(() => import("../fleet"));
const Documents = lazy(() => import("../documents/Documents"));
const PartsOrders = lazy(()=> import("../parts-orders/Orders"))
const Timesheets = lazy(() => import("../timesheets"));
const Production = lazy(() => import("../production"));
const PartsRequisition = lazy(() => import("../parts-requisition"));
const ActionRegister = lazy(()=> import("../action_register/ActionRegister"))
const SalesOrder = lazy(()=> import("../salesorder/SalesOrderList"))
const WorkOrder = lazy(() => import("../workorder/WorkOrderList"))
const Jobs = lazy(() => import("../jobs/JobList"));
const FollowUp = lazy(()=> import("../followups/FollowUp"))
const Quotes = lazy(() => import("../quotes/Quotes"));
const FinancialReports = lazy(() => import("../financial-reports"));
const Freight = lazy(() => import("../freight"));
const Groups = lazy(() => import("../groups"));
const Currency = lazy(() => import("../currency/index"));
const Certificates = lazy(() => import("../certificates"));
const Orders = lazy(() => import("../orders/Orders"));
const ReportsAndForms = lazy(() => import("../reports-and-forms"));
const ServiceOrders = lazy(() => import("../service-orders"));
const InwardsGoods = lazy(() => import("../inwards-goods"));
const MaintenanceLog = lazy(() => import("../maintenance-log"));
const Modifications = lazy(() => import("../modifications"));
const RoleEquipment = lazy(() => import("../role-equipment"));
const TechLog = lazy(() => import("../tech-log"));
const Trend = lazy(() => import("../trend"));
const SystemConfig = lazy(() => import("../system-config"));
const SystemReports = lazy(() => import("../system-reports"));
const Personnel = lazy(() => import("../personnel"));
const History = lazy(() => import("../history"));
const Manifest = lazy(() => import("../manifest"));
const Companies = lazy(() => import("../companies/CompanyList"));
const SupplierInvoices = lazy(() => import("../supplier-invoices/SupplierInvoices"));
// Accounts Exports (H255) — static route so /app/xero/batches' redirect lands
// before (and without) the nav row; the nav row routes here too.
const BatchExports = lazy(() => import("../batchexports"));


const HelpChat = lazy(() => import("../help-chat/HelpChat"));
const ConfluenceHelpCenter = lazy(() => import("../help/ConfluenceHelpCenter"));
const LandingPage = lazy(() => import("../help/LandingPage"));
const Hub = lazy(() => import("../hub/index"));
const ReleaseNotesCenter = lazy(() => import("../release-notes/ReleaseNotesCenter"));
const RoadmapCenter = lazy(() => import("../roadmap/RoadmapCenter"));
const TicketPortal = lazy(() => import("../ticket-portal/TicketPortal"));
const FeatureRequestsPortal = lazy(() => import("../feature-requests/TicketPortal"));
// const Onboarding = lazy(() => import("../onboarding/Onboarding"));

const DirectoryDashboard = lazy(() => import("../directory/DirectoryDashboard"));
const SalesDashboard = lazy(() => import("../sections/SalesDashboard"));
const PurchasingDashboard = lazy(() => import("../sections/PurchasingDashboard"));
const TechRecordsDashboard = lazy(() => import("../sections/TechRecordsDashboard"));
const FlightAdminDashboard = lazy(() => import("../sections/FlightAdminDashboard"));
const MaintenanceDashboard = lazy(() => import("../sections/MaintenanceDashboard"));
const StoresDashboard = lazy(() => import("../sections/StoresDashboard"));
const FinanceDashboard = lazy(() => import("../sections/FinanceDashboard"));
const Journals = lazy(() => import("../journals/Journals"));
const SafetyQualityDashboard = lazy(() => import("../sections/SafetyQualityDashboard"));
const SystemAdminDashboard = lazy(() => import("../sections/SystemAdminDashboard"));
const PilotsDashboard = lazy(() => import("../sections/PilotsDashboard"));
const Weather = lazy(() => import("../weather"));
const XeroCallback = lazy(() => import("../xero/XeroCallback"));
const XeroSettings = lazy(() => import("../xero/XeroSettings"));
const Transpond = lazy(() => import("../transpond"));
const Microsoft365 = lazy(() => import("../integrations/Microsoft365"));
const Sage300 = lazy(() => import("../integrations/Sage300"));
const Laserfiche = lazy(() => import("../integrations/Laserfiche"));
const ModuleLandingPage = lazy(() => import("../components/ModuleLandingPage"));
const MobileEmulator = lazy(() => import("../mobile-emulator/MobileEmulator"));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const TitleSetter: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => {
  const { setTitle } = usePageTitle();
  useEffect(() => {
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);
  return <>{children}</>;
};

// Cognito config is resolved PER TENANT at runtime from the request Host
// (src/config/product.ts) — never from build-time env: one image serves every
// tenant, so a baked pool/client would be wrong for all but one of them.
// GCIP tenants are configured lazily by src/auth instead.
if (getAuthProvider() === "cognito") {
  const cognito = getCognitoConfig();
  Amplify.configure({
    Auth: {
      identityPoolId: cognito.identityPoolId,
      region: cognito.region,
      userPoolId: cognito.userPoolId,
      userPoolWebClientId: cognito.userPoolWebClientId,
      oauth: {
        domain: cognito.domain,
        scope: [
          "phone",
          "email",
          "profile",
          "openid",
          "aws.cognito.signin.user.admin",
        ],
        redirectSignIn: cognito.redirectSignIn,
        redirectSignOut: cognito.redirectSignOut,
        responseType: "code", // or 'token', note that REFRESH token will only be generated when the responseType is code
      },
    },
  });
}

/** Redirects legacy /help-center/:pageId to canonical /aeronet-help/:pageId preserving the id. */
const HelpCenterPageRedirect: React.FC = () => {
  const { pageId = '' } = useParams<{ pageId: string }>();
  return <Navigate to={`/aeronet-help/${pageId}`} replace />;
};

/** Redirects legacy /help-v2/:pageId to canonical /hub/:pageId preserving the id. */
const HubPageRedirect: React.FC = () => {
  const { pageId = '' } = useParams<{ pageId: string }>();
  return <Navigate to={`/hub/${pageId}`} replace />;
};

/** Generic landing stub for top-nav sections without a built dashboard —
 *  the dropdown home item always lands somewhere (Menu.tsx falls back here). */
const SectionLanding: React.FC = () => {
  const { sectionName = "" } = useParams<{ sectionName: string }>();
  const title = decodeURIComponent(sectionName) || "Home";
  return (
    <TitleSetter title={title}>
      <ModuleLandingPage title={title} />
    </TitleSetter>
  );
};

interface AppProps {
  authState?: string;
  authData?: any;
  hideLoader?: () => void;
}

const mkNavItem = (id: string, name: string, fn: string, to: string): IAeronetNavItem => {
  const item: IAeronetNavItem = {
    id,
    name,
    function: fn,
    to,
    target: "",
    remote_menu_key: "",
    url: "",
  };
  // Generic: any of these rows whose `fn` matches a src/app/navModeRegistry.ts
  // entry picks up that module's mode list as `subMenus` for free — same
  // mechanism as the live-nav loop below, so the fallback nav doesn't need a
  // hand-typed subMenus copy either.
  applyNavModeChildren(item, fn);
  return item;
};

const DEFAULT_G2_NAV: IAeronetNav[] = [
  {
    id: "directory",
    name: "Directory",
    children: [
      mkNavItem("AddressBook", "Address Book", "AddressBook", "AddressBook"),
      mkNavItem("contacts", "Contacts", "contacts", "contacts"),
      mkNavItem("groups", "Groups", "groups", "groups"),
      mkNavItem("user_management", "User Management", "user_management", "user_management"),
    ],
  },
  {
    id: "sales",
    name: "Sales",
    children: [
      mkNavItem("customer_quotes", "Customer Quotes", "customer_quotes", "customer_quotes"),
      // `customer_quotes`, matching the key live g2 actually serves from
      // /v6/nav — this fallback said `quotes`, so the module mounted at a
      // DIFFERENT path whenever the nav call failed, and the RFQ Process
      // deep link (src/quotes/pendingRfq.ts) would 404 against it.
      // Features.tsx maps both keys to the same component.
      mkNavItem("opportunities", "Opportunities", "opportunities", "opportunities"),
      mkNavItem("work-orders", "Work Orders", "work-orders", "work-orders"),
      mkNavItem("invoices", "Invoices", "invoices", "invoices"),
    ],
  },
  {
    id: "purchasing",
    name: "Purchasing",
    children: [
      mkNavItem("orders", "Purchase Orders", "orders", "orders"),
      mkNavItem("supplier_quotes", "Supplier Quotes", "supplier_quotes", "supplier_quotes"),
      mkNavItem("freight", "Freight", "freight", "freight"),
      mkNavItem("parts-orders", "Parts Orders", "parts-orders", "parts-orders"),
      mkNavItem("supplier_invoices", "Supplier Invoices", "supplier_invoices", "supplier_invoices"),
    ],
  },
  {
    id: "tech_records",
    name: "Tech Records",
    children: [
      mkNavItem("aircraft", "Aircraft", "aircraft", "aircraft"),
      mkNavItem("fleet", "Fleet Report", "fleet", "fleet"),
      mkNavItem("ads-and-sbs", "ADs and SBs", "ads-and-sbs", "ads-and-sbs"),
      mkNavItem("inspections", "Inspections", "inspections", "inspections"),
      mkNavItem("modifications", "Modifications", "modifications", "modifications"),
      mkNavItem("components", "Components", "components", "components"),
      mkNavItem("aircraft_configuration", "Aircraft Configuration", "aircraft_configuration", "aircraft_configuration"),
      mkNavItem("log-book", "Flight Logbook", "log-book", "log-book"),
      mkNavItem("tech-log", "Tech Log", "tech-log", "tech-log"),
      mkNavItem("maintenance-log", "Maintenance Log", "maintenance-log", "maintenance-log"),
      mkNavItem("defect-reporting", "Defect Reporting", "defect-reporting", "defect-reporting"),
      mkNavItem("ata-chapters", "ATA Chapters", "ata-chapters", "ata-chapters"),
      mkNavItem("ground-equipment", "Ground Equipment", "ground-equipment", "ground-equipment"),
      mkNavItem("role-equipment", "Role Equipment", "role-equipment", "role-equipment"),
      mkNavItem("trend", "Engine Trend", "trend", "trend"),
    ],
  },
  {
    id: "maintenance",
    name: "Maintenance",
    children: [
      mkNavItem("maintenance", "Maintenance", "maintenance", "maintenance"),
      // `aircraft_configuration`, NOT the hyphenated spelling — Features.tsx
      // registers it at :325 with an underscore. Found by the key-agreement
      // test below, not by review.
      mkNavItem("aircraft_configuration", "Aircraft Configuration", "aircraft_configuration", "aircraft_configuration"),
      mkNavItem("ads-and-sbs", "ADs and SBs", "ads-and-sbs", "ads-and-sbs"),
      mkNavItem("inspections", "Inspections", "inspections", "inspections"),
      mkNavItem("modifications", "Modifications", "modifications", "modifications"),
      mkNavItem("work-orders", "MRO Work Orders", "work-orders", "work-orders"),
      // The FEATURE key for the Service Orders module is `tds` — its legacy
      // mod_function — registered at Features.tsx:461 (component ServiceOrders,
      // src/service-orders/). `service-orders` matched no feature, AND the only
      // static route for the module is `serviceorders/*` (App.tsx:806, no
      // hyphen), so the hyphenated spelling matched NEITHER path and 404'd.
      // `tds` is in the GCIP switch above, which sets to = function, so live g2
      // serves this at /app/tds.
      mkNavItem("tds", "Service Orders", "tds", "tds"),
      mkNavItem("freight", "Freight", "freight", "freight"),
      mkNavItem("toolrequests", "Tool Requests", "toolrequests", "toolrequests"),
    ],
  },
  {
    id: "stores",
    name: "Stores",
    children: [
      mkNavItem("batches", "Batches", "batches", "batches"),
      mkNavItem("core", "Cores", "core", "core"),
      mkNavItem("tools", "Calibrated Tools", "tools", "tools"),
    ],
  },
  {
    id: "finance",
    name: "Finance",
    children: [
      mkNavItem("rates", "User Rates", "rates", "rates"),
      mkNavItem("invoices", "Invoices", "invoices", "invoices"),
      mkNavItem("accounts", "Chart of Accounts", "accounts", "accounts"),
      mkNavItem("journals", "Journals", "journals", "journals"),
      mkNavItem("batchexports", "Accounts Exports", "batchexports", "batchexports"),
      mkNavItem("currency", "Currency", "currency", "currency"),
      // The FEATURE key is `job_costs` (Features.tsx:281 -> FinancialReports),
      // and on GCIP tenants App.tsx's `case "job_costs"` sets to = function, so
      // live g2 serves this at /app/job_costs. `financial-reports` matched no
      // feature and mounted nothing. Also found by the test below.
      mkNavItem("job_costs", "Financial Reports", "job_costs", "job_costs"),
    ],
  },
  {
    id: "safety_quality",
    name: "Safety & Quality",
    children: [
      mkNavItem("actionregister", "Action Register", "actionregister", "actionregister"),
      mkNavItem("audits", "Audits", "audits", "audits"),
      mkNavItem("meetings", "Meetings", "meetings", "meetings"),
      mkNavItem("documents", "Documents", "documents", "documents"),
      mkNavItem("followups", "Follow Ups", "followups", "followups"),
      mkNavItem("ncr", "OFIs", "ncr", "ncr"),
      mkNavItem("registers", "Registers", "registers", "registers"),
      mkNavItem("risk", "Risk Register", "risk", "risk"),
      mkNavItem("module_stationery", "Reports and Forms", "module_stationery", "reports-and-forms"),
      mkNavItem("due_date_control", "Due Date Control", "due_date_control", "due-date-control"),
    ],
  },
  {
    id: "system_admin",
    name: "System Admin",
    children: [
      mkNavItem("xero", "Xero", "xero", "xero"),
      mkNavItem("personnel", "Personnel", "personnel", "personnel"),
      mkNavItem("users_prefs", "Preferences", "users_prefs", "preferences"),
    ],
  },
  {
    id: "flight_admin",
    name: "Flight Admin",
    children: [
      mkNavItem("aircraft_roster", "Aircraft Roster", "aircraft_roster", "aircraft-roster"),
      mkNavItem("flight_booking", "Bookings", "flight_booking", "flight_booking"),
      mkNavItem("flight_expenses", "Expenses", "flight_expenses", "flight_expenses"),
      mkNavItem("flightduty", "Flight & Duty", "flightduty", "flightduty"),
      mkNavItem("flight_following", "Flight Following", "flight_following", "flight_following"),
      // `flightplan`, NOT `flight_plan` — the key live g2 serves from /v6/nav
      // (module_g2_demo row 10282, mod_function `flightplan`) and the only one
      // Features.tsx registers (:183). The underscored spelling resolved to no
      // feature, so getFeature() missed and NO <Route> was emitted: the item
      // rendered and then fell through to the `path="*"` NotFound. Same defect
      // as `quotes`/`customer_quotes` above, which is why that comment exists.
      mkNavItem("flightplan", "Flight Plan", "flightplan", "flightplan"),
      mkNavItem("flight_schedule", "Flight Schedule", "flight_schedule", "flight_schedule"),
      mkNavItem("flights", "Flights", "flights", "flights"),
      mkNavItem("fuel_tanks", "Fuel", "fuel_tanks", "fuel_tanks"),
      mkNavItem("landings", "Landings", "landings", "landings"),
      mkNavItem("load_sheet", "Load Sheets", "load_sheet", "load_sheet"),
      mkNavItem("flight_loc", "Locations", "flight_loc", "flight_loc"),
      mkNavItem("operations_reports", "Operations Reports", "operations_reports", "operations_reports"),
      // NO "OPS Personnel" ENTRY. Removed 2026-09-03, and NOT replaceable with
      // a corrected key -- both available spellings are wrong:
      //   `ops_personnel` (what was here) matches no Features.tsx entry, so it
      //      emitted no <Route> and 404'd through the catch-all.
      //   `personnel` (what live g2 now serves -- module_g2_demo row 519 was
      //      repointed to mod_function `personnel` on 2026-08-29) is ALREADY in
      //      this fallback under System Admin, so using it here would emit a
      //      SECOND `personnel/*` route to the identical component and carry
      //      live g2's 519-vs-272 nav-row collision into the fallback.
      // That collision -- OPS Personnel opening Directory's Personnel screen --
      // is a live g2 defect tracked separately, and closing it is a product
      // decision (retire 519, or build a distinct OPS Personnel module), not a
      // fallback-nav decision. Until it is decided this fallback cannot
      // represent the item honestly, so it omits it; the Personnel screen stays
      // reachable via System Admin above.
      mkNavItem("manifest", "Pax Manifest", "manifest", "manifest"),
      mkNavItem("trips", "Trips", "trips", "trips"),
      mkNavItem("vehicles", "Vehicles", "vehicles", "vehicles"),
    ],
  },
  {
    id: "pilots",
    name: "Pilots",
    children: [
      mkNavItem("flightduty", "Flight & Duty", "flightduty", "flightduty"),
      // Second `flight_plan` site — the Pilots section had the same unbound key
      // as Flight Admin above. Review named only the Flight Admin one; a
      // grep for the key found both, which is why the test below asserts over
      // EVERY entry rather than the two that were reported.
      mkNavItem("flightplan", "Flight Plan", "flightplan", "flightplan"),
      mkNavItem("flights", "Flights", "flights", "flights"),
      mkNavItem("fuel_tanks", "Fuel", "fuel_tanks", "fuel_tanks"),
      mkNavItem("load_sheet", "Load Sheets", "load_sheet", "load_sheet"),
      mkNavItem("pilot_roster", "Pilot Roster", "pilot_roster", "pilot_roster"),
      mkNavItem("timesheets", "Pilot Timesheets", "timesheets", "timesheets"),
      mkNavItem("weather", "Weather", "weather", "weather"),
    ],
  },
];

const App: React.FC<AppProps> = ({ authData, authState, hideLoader }) => {
  const [nav, setNav] = useState<readonly IAeronetNav[]>([]);
  const { isFeatureActive, getFeature } = useContext(FeatureContext);

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const currentUser = useCurrentUser();
  const displayAlias = useDisplayAlias();

  // Sign-in bookkeeping — last_login and Invited → Active — decided by the
  // server on POST /v6/users/me/signed-in, once per browser session, quietly
  // (H201; see src/app/signInWrites.ts).
  useEffect(() => {
    if (authState !== "signedIn" || !currentUser?.id) return;
    recordSignIn(currentUser);
  }, [authState, currentUser]);

  // Back to the page the user was on when their session ran out (or when a
  // deep link sent them to sign in): the login round trip lands on /app/.
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (authState !== "signedIn") return;
    const returnTo = takeReturnTo();
    if (returnTo && returnTo !== `${location.pathname}${location.search}${location.hash}`) {
      navigate(returnTo, { replace: true });
    }
    // Once per sign-in, not on every navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  useEffect(() => {
    document.title = `Aeronet - ${displayAlias || "Aeronet"}`;
  }, [displayAlias]);
  
  if (hideLoader) {
    hideLoader();
  }

  useEffect(() => {
    let active = true;

    async function setUpNav() {
      let cognitoUser: any = null;
      try {
        cognitoUser = await getSessionUser();
        if (cognitoUser?.attributes) {
          Sentry.setUser({
            id: cognitoUser.attributes["custom:aeronet_user_id"],
            username: cognitoUser.username,
            email: cognitoUser.attributes.email,
            ...cognitoUser.attributes,
          });
        }
      } catch (err) {
        // Local dev or unauthenticated session — proceed with nav fetch
      }

      let navUrl = "/v6/nav";
      const userId = cognitoUser?.attributes?.["custom:aeronet_user_id"];
      if (
        userId &&
        process.env.NODE_ENV === "development" &&
        process.env.REACT_APP_API_URL === "http://localhost:9999"
      ) {
        // call api with user id when running locally as the api doesn't get the jwt decoded by api gateway
        navUrl = navUrl + "/" + userId;
        console.warn("Using alternate get_nav_by_id api");
      }

      try {
        const navUserId = cognitoUser?.attributes?.["custom:aeronet_user_id"] ?? "local";
        const resp: AxiosResponse<AeronetV6Response<IAeronetNav>> =
          // Shared with useSectionReachability (app/navRequest.ts): one fetch.
          await axios.get(navUrl, navRequestConfig(navUserId));

        // this sets up the paths to React views for features that are active
        // the default is that the React view uses the same path as the function name in Roxen
        // this is the place to override that
        for (const navItem of resp.data.data) {
          for (const menuItem of navItem.children) {
            if (menuItem.function !== "url" && menuItem.function !== "custom") {
              if (isFeatureActive(menuItem.function)) {
                switch (menuItem.function) {
                  case "users_prefs":
                    menuItem.to = "preferences";
                    break;
                  case "module_stationery":
                    menuItem.to = "reports-and-forms";
                    menuItem.name = "Reports and Forms";
                    break;
                  case "jobs":
                  case "quotes":
                  case "documents":
                  case "freight":
                  case "groups":
                  case "currency":
                  case "certs":
                  case "orders":
                  case "tds":
                  case "inwards":
                  case "job_costs":
                    // Cognito products on legacy installs (scm/crm/ops/mt/mro) keep the legacy
                    // Roxen href for these. Pure v6 tenants (g2, aquipe, demo, etc.) have NO
                    // legacy install — every menu item lands on its v6 module (Aaron 2026-08-22:
                    // "all should be app/").
                    if (isPureV6Tenant()) {
                      menuItem.to = menuItem.function;
                    }
                    break;
                  case "system_config":
                  case "system-config":
                  case "systemconfig":
                  case "types":
                    menuItem.to = "system-config";
                    menuItem.name = "System Config";
                    break;
                  case "reports":
                  case "system_reports":
                  case "system-reports":
                    menuItem.to = "reports";
                    menuItem.name = "Reports";
                    break;
                  case "service_requests":
                  case "toolrequests":
                    menuItem.to = "toolrequests";
                    menuItem.name = "Tool Requests";
                    break;
                  case MOBILE_EMULATOR_FUNCTION:
                    // The row's url_query picks the emulator view (M4).
                    menuItem.to = mobileEmulatorTarget(menuItem.url);
                    break;
                  default:
                    menuItem.to = menuItem.function;
                }
                applyNavModeChildren(menuItem, menuItem.function);
              }
            } else {
              if (isFeatureActive(menuItem.remote_menu_key)) {
                switch (menuItem.remote_menu_key) {
                  case "module_stationery":
                    menuItem.to = "reports-and-forms";
                    menuItem.name = "Reports and Forms";
                    break;
                  case "financereports":
                    // Land on the dashboard directly — matches legacy
                    // /app/financereports/dashboard (Aaron 2026-08-22).
                    menuItem.to = "financereports/dashboard";
                    break;
                  case "system_config":
                  case "system-config":
                  case "systemconfig":
                  case "types":
                    menuItem.to = "system-config";
                    menuItem.name = "System Config";
                    break;
                  case "reports":
                  case "system_reports":
                  case "system-reports":
                    menuItem.to = "reports";
                    menuItem.name = "Reports";
                    break;
                  case "service_requests":
                  case "toolrequests":
                    menuItem.to = "toolrequests";
                    menuItem.name = "Tool Requests";
                    break;
                  default:
                    menuItem.to = menuItem.remote_menu_key;
                }
                // Generic: subMenus for any mode-bearing module come from the
                // module's OWN mode list (src/app/navModeRegistry.ts), not a
                // hand-typed copy per case above — that hand-typed copy is
                // what let Inventory's nav list drift out of sync with
                // InventoryLayout's real modes. A miss here is a no-op.
                applyNavModeChildren(menuItem, menuItem.remote_menu_key);
              }
            }
          }
        }
        if (active) {
          // G2 demo only: hide aviation-ops menus Denis doesn't demo. Nav ids
          const navData = sanitizeMaintenanceNav(filterNavForTenant(resp.data.data || []));
          setNav(navData);
        }
      } catch (e: any) {
        console.error("Error setting up nav:", e);
        if (getAuthProvider() === "gcip" && e?.response?.status === 401) {
          gcipSignOut();
          window.location.reload();
          return;
        }
        // Static fallback nav is a G2-DEMO convenience only. Never install it
        // on another tenant: aquipe (production) got G2's aviation menu on a
        // transient nav error, 2026-08-21. Other tenants keep an empty nav and
        // surface the failure instead.
        if (active && getProduct() === "g2") {
          setNav(DEFAULT_G2_NAV);
        }
        return;
      }
    }

    // GcipGate and the local-dev bypass both mount <App authState="signedIn">,
    // so this single check covers Cognito, GCIP and local. Do NOT widen it with
    // env-var sniffing: the unified image bakes no REACT_APP_COGNITO_* vars, so
    // "pool id missing" is true in production and ran nav setup pre-login.
    if (authState === "signedIn") {
      setUpNav();
    }

    return () => {
      active = false;
    };
  }, [authState, isFeatureActive]);

  return (
    <PageTitleProvider>
      {/* Cold-start explanation + "Keep awake 1h/2h/4h", only after sign-in (src/app/wake.ts). */}
      <WakeGate signedIn={authState === "signedIn"} />
      {/* The sign-off password check the API asks for with a 428 (src/auth/stepUp.ts). */}
      {authState === "signedIn" ? <StepUpHost /> : null}
      <SentryRoutes>
        <Route element={<AppLayout nav={nav} />}>
        {nav.length > 0 ?  <Route path="/" element={<TitleSetter title="Dashboard"><Dashboard /></TitleSetter>} /> : <Route path="/" element={<TitleSetter title="Loading"><LoadingView /></TitleSetter>} />}

     
        <Route path="help-chat/*" element={<HelpChat />} />

        {/* Canonical Help Center */}
        <Route path="aeronet-help/*" element={<LandingPage />} />
        <Route path="aeronet-help/:pageId" element={<ConfluenceHelpCenter />} />
        {/* Legacy aliases → canonical (kept for in-content links & bookmarks) */}
        <Route path="help-center/:pageId" element={<HelpCenterPageRedirect />} />
        <Route path="help-center/*" element={<Navigate to="/aeronet-help" replace />} />
        <Route path="help/*" element={<Navigate to="/aeronet-help" replace />} />

        <Route path="hub/:pageId" element={<Hub />} />
        <Route path="hub/*" element={<Hub />} />
        {/* Legacy alias → canonical Hub (kept for in-content links & bookmarks) */}
        <Route path="help-v2/:pageId" element={<HubPageRedirect />} />
        <Route path="help-v2/*" element={<Navigate to="/hub" replace />} />
        {/* Chat citations deep-link to the API/space path /app/aeronet-sops/<id>
            (the SOP space name). The SOP UI lives at /hub, so map it across —
            also covers citation links already persisted in users' localStorage. */}
        <Route path="aeronet-sops/:pageId" element={<HubPageRedirect />} />
        <Route path="aeronet-sops/*" element={<Navigate to="/hub" replace />} />

        {/* Canonical Release Notes */}
        <Route path="aeronet-release-notes/*" element={<ReleaseNotesCenter />} />
        <Route path="aeronet-release-notes/:pageId" element={<ReleaseNotesCenter />} />
        {/* Legacy alias → canonical */}
        <Route path="release-notes/*" element={<Navigate to="/aeronet-release-notes" replace />} />
        <Route path="release-notes/:pageId" element={<ReleaseNotesCenter />} />

        <Route path="aeronet-roadmap/*" element={<RoadmapCenter />} />
        <Route path="aeronet-roadmap/:pageId" element={<RoadmapCenter />} />

        <Route path="ticket-portal/*" element={<TicketPortal />} />
        <Route path="feature-requests/*" element={<FeatureRequestsPortal />} />
        <Route path="mobile-emulator/*" element={<TitleSetter title="Mobile Emulator"><MobileEmulator /></TitleSetter>} />
        <Route path="mobile/*" element={<TitleSetter title="Mobile Emulator"><MobileEmulator /></TitleSetter>} />

        {/* Section Landing Dashboards — SectionHomeLayout renders the shell
            breadcrumb ("<Module>" crumb, no mode dropdown: these are
            tab-based homes, not mode-switched modules); SectionDashboard
            itself no longer renders a ListingHeader (WP5b cleanup). */}
        <Route path="directory/*" element={<TitleSetter title="Directory"><SectionHomeLayout title="Directory"><DirectoryDashboard originalUrl="" title="Directory" /></SectionHomeLayout></TitleSetter>} />
        <Route path="sales/*" element={<TitleSetter title="Sales"><SectionHomeLayout title="Sales"><SalesDashboard originalUrl="" title="Sales" /></SectionHomeLayout></TitleSetter>} />
        <Route path="purchasing/*" element={<TitleSetter title="Purchasing"><SectionHomeLayout title="Purchasing"><PurchasingDashboard originalUrl="" title="Purchasing" /></SectionHomeLayout></TitleSetter>} />
        <Route path="tech-records/*" element={<TitleSetter title="Tech Records"><SectionHomeLayout title="Tech Records"><TechRecordsDashboard originalUrl="" title="Tech Records" /></SectionHomeLayout></TitleSetter>} />
        <Route path="maintenance/*" element={<TitleSetter title="Maintenance"><SectionHomeLayout title="Maintenance"><MaintenanceDashboard originalUrl="" title="Maintenance" /></SectionHomeLayout></TitleSetter>} />
        <Route path="stores/*" element={<TitleSetter title="Stores"><SectionHomeLayout title="Stores"><StoresDashboard originalUrl="" title="Stores" /></SectionHomeLayout></TitleSetter>} />
        <Route path="finance/*" element={<TitleSetter title="Finance"><SectionHomeLayout title="Finance"><FinanceDashboard originalUrl="" title="Finance" /></SectionHomeLayout></TitleSetter>} />
        <Route path="safety-quality/*" element={<TitleSetter title="Safety & Quality"><SectionHomeLayout title="Safety & Quality"><SafetyQualityDashboard originalUrl="" title="Safety & Quality" /></SectionHomeLayout></TitleSetter>} />
        <Route path="system-admin/*" element={<TitleSetter title="System Admin"><SectionHomeLayout title="System Admin"><SystemAdminDashboard originalUrl="" title="System Admin" /></SectionHomeLayout></TitleSetter>} />
        {/* Pilots. Same shape as the eight above, and deliberately at slug
            "pilots" — NOT "landing/Pilots", which is Menu.tsx's fallback for a
            section with no built home. Pilots and Flight Admin are the same
            menu separated by permission (an admin sees all flights, a pilot
            sees their own), so this route intentionally reuses the shared
            module routes rather than opening a parallel /pilots/* tree. */}
        <Route path="pilots/*" element={<TitleSetter title="Pilots"><SectionHomeLayout title="Pilots"><PilotsDashboard originalUrl="" title="Pilots" /></SectionHomeLayout></TitleSetter>} />
        {/* Flight Admin. Same rule as Pilots above: slug "flight-admin", never
            "landing/Flight Admin". Its destinations are gated on the live nav
            (SectionConfig.gateOnNav) because most of this menu is built and
            endpoint-backed but provisioned per tenant. */}
        <Route path="flight-admin/*" element={<TitleSetter title="Flight Admin"><SectionHomeLayout title="Flight Admin"><FlightAdminDashboard originalUrl="" title="Flight Admin" /></SectionHomeLayout></TitleSetter>} />

        {/* <Route path="onboarding" element={<Onboarding />} /> */}


        {isFeatureActive("user_settings") && (
          <Route path="settings" element={<Settings />} />
        )}
        {isFeatureActive("tools") && (
          <Route
            path="tools/*"
            element={<Tools originalUrl="" title="Tools" />}
          />
        )}
        <Route
          path="sors/*"
          element={
            <TitleSetter title="Sors">
          <Sors originalUrl={''}
          title={`Service & Operations Report`}/>
          </TitleSetter>
          }
        />
        <Route
          path="aircraft/*"
          element={
            <TitleSetter title="Aircraft">
              <Aircraft originalUrl={""} title={`Aircraft`} />
            </TitleSetter>
          }
        />
        <Route
          path="invoices/*"
          element={
            <TitleSetter title="Invoices">
              <Invoices originalUrl={""} title={`Invoices`} />
            </TitleSetter>
          }
        />
        <Route
          path="journals/*"
          element={
            <TitleSetter title="Journals">
              <Journals originalUrl={""} title={`Journals`} />
            </TitleSetter>
          }
        />
        <Route
          path="finance/journals/*"
          element={
            <TitleSetter title="Journals">
              <Journals originalUrl={""} title={`Journals`} />
            </TitleSetter>
          }
        />
        <Route
          path="batchexports/*"
          element={
            <TitleSetter title="Accounts Exports">
              <BatchExports originalUrl={""} title="Accounts Exports" />
            </TitleSetter>
          }
        />
        <Route
          path="quotes/*"
          element={
            <TitleSetter title="Quotes">
              <Quotes originalUrl={""} title="Quotes" />
            </TitleSetter>
          }
        />
        {/* Finance — /app/financereports → dashboard; /app/financereports/reports. Nav submenu like Inventory. */}
        <Route
          path="financereports/*"
          element={
            <TitleSetter title="Financial Reports">
              <FinancialReports originalUrl="" title="Financial Reports" />
            </TitleSetter>
          }
        />
        <Route
          path="financial-reports/*"
          element={
            <TitleSetter title="Financial Reports">
              <FinancialReports originalUrl="" title="Financial Reports" />
            </TitleSetter>
          }
        />
        <Route
          path="freight/*"
          element={
            <TitleSetter title="Freight">
              <Freight originalUrl={""} title="Freight" />
            </TitleSetter>
          }
        />
        <Route
          path="groups/*"
          element={
            <TitleSetter title="Groups">
              <Groups originalUrl={""} title="Groups" />
            </TitleSetter>
          }
        />
        <Route
          path="currency/*"
          element={
            <TitleSetter title="Currency">
              <Currency originalUrl={""} title="Currency" />
            </TitleSetter>
          }
        />
        <Route
          path="certs/*"
          element={
            <TitleSetter title="Certificates">
              <Certificates originalUrl={""} title="Certificates" />
            </TitleSetter>
          }
        />
        <Route
          path="serviceorders/*"
          element={
            <TitleSetter title="Service Orders">
              <ServiceOrders originalUrl="" title="Service Orders" />
            </TitleSetter>
          }
        />

        <Route
          path="inwardsgoods/*"
          element={
            <TitleSetter title="Inwards Goods">
              <InwardsGoods originalUrl="" title="Inwards Goods" />
            </TitleSetter>
          }
        />
        <Route
          path="inwards-goods/*"
          element={
            <TitleSetter title="Inwards Goods">
              <InwardsGoods originalUrl="" title="Inwards Goods" />
            </TitleSetter>
          }
        />
        <Route
          path="inwards/*"
          element={
            <TitleSetter title="Inwards Goods">
              <InwardsGoods originalUrl="" title="Inwards Goods" />
            </TitleSetter>
          }
        />
        <Route
          path="orders/*"
          element={
            <TitleSetter title="Purchase Orders">
              <Orders originalUrl={""} title="Purchase Orders" />
            </TitleSetter>
          }
        />
        <Route
          path="documents/*"
          element={<Documents originalUrl={""} title={`Documents`} />}
        />
        <Route
          path="components/*"
          element={
            <TitleSetter title="Components">
              <Components originalUrl={""} title={`Components`} />
            </TitleSetter>
          }
        />
        <Route
          path="inspections/*"
          element={
            <TitleSetter title="Inspections">
              <Inspections originalUrl={""} title={`Inspections`} />
            </TitleSetter>
          }
        />
        <Route
          path="inspections_tr/*"
          element={
            <TitleSetter title="Inspections">
              <InspectionsTR originalUrl={""} title={`Inspections`} />
            </TitleSetter>
          }
        />
        <Route
          path="modifications/*"
          element={
            <TitleSetter title="Modifications">
              <Modifications originalUrl={""} title={`Modifications`} />
            </TitleSetter>
          }
        />
        <Route
          path="modification/*"
          element={
            <TitleSetter title="Modifications">
              <Modifications originalUrl={""} title={`Modifications`} />
            </TitleSetter>
          }
        />
        <Route
          path="fleet/*"
          element={
            <TitleSetter title="Fleet Report">
              <Fleet originalUrl={""} title={`Fleet Report`} />
            </TitleSetter>
          }
        />
        <Route
          path="weather/*"
          element={
            <TitleSetter title="Weather">
              <Weather originalUrl="" title="Weather" />
            </TitleSetter>
          }
        />
        <Route
          path="pilots/weather/*"
          element={
            <TitleSetter title="Weather">
              <Weather originalUrl="" title="Weather" />
            </TitleSetter>
          }
        />
        <Route
          path="systemreports/*"
          element={
            <TitleSetter title="System Reports">
              <Reports />
            </TitleSetter>
          }
        />
        <Route
          path="partsorders/*"
          element={
            <TitleSetter title="Parts Orders">
              <PartsOrders  originalUrl={""} title={`Parts Orders`} />
            </TitleSetter>
          }
        />
        <Route
          path="parts-orders/*"
          element={
            <TitleSetter title="Parts Orders">
              <PartsOrders originalUrl={""} title={`Parts Orders`} />
            </TitleSetter>
          }
        />
        <Route
          path="timesheets/*"
          element={
            <TitleSetter title="Time Sheets">
              <Timesheets originalUrl={""} title={`Time Sheets`} />
            </TitleSetter>
          }
        />
        <Route
          path="time-sheets/*"
          element={
            <TitleSetter title="Time Sheets">
              <Timesheets originalUrl={""} title={`Time Sheets`} />
            </TitleSetter>
          }
        />
        <Route
          path="production/*"
          element={
            <TitleSetter title="Production">
              <Production originalUrl={""} title={`Production`} />
            </TitleSetter>
          }
        />
        <Route
          path="production-planning/*"
          element={
            <TitleSetter title="Production">
              <Production originalUrl={""} title={`Production`} />
            </TitleSetter>
          }
        />
        <Route
          path="parts-requisition/*"
          element={
            <TitleSetter title="Parts Requisition">
              <PartsRequisition originalUrl={""} title={`Parts Requisition`} />
            </TitleSetter>
          }
        />
        <Route
          path="partsrequisitions/*"
          element={
            <TitleSetter title="Parts Requisition">
              <PartsRequisition originalUrl={""} title={`Parts Requisition`} />
            </TitleSetter>
          }
        />
        <Route path="duedate/*" element = {<DueDate originalUrl="" title="Due Date Control"/>}/>
        <Route path="due_date_control/*" element = {<DueDate originalUrl="" title="Due Date Control"/>}/>
        <Route path="due-date-control/*" element = {<DueDate originalUrl="" title="Due Date Control"/>}/>
        <Route path="whiteboard/*" element = {<DueDate originalUrl="" title="Due Date Control"/>}/>
        <Route
          path="salesorders/*"
          element={
            <TitleSetter title="Sales Orders">
              <SalesOrder originalUrl="" title="Sales Orders" />
            </TitleSetter>
          }
        />
        <Route
          path="workorders/*"
          element={
            <TitleSetter title="Work Orders">
              <WorkOrder originalUrl="" title="Work Orders" />
            </TitleSetter>
          }
        />
        <Route
          path="work-orders/*"
          element={
            <TitleSetter title="Work Orders">
              <WorkOrder originalUrl="" title="Work Orders" />
            </TitleSetter>
          }
        />
        <Route
          path="jobs/*"
          element={
            <TitleSetter title="Jobs">
              <Jobs originalUrl="" title="Jobs" />
            </TitleSetter>
          }
        />
        {isFeatureActive("module_stationery") && (
          <Route
            path="reports-and-forms/*"
            element={
              <TitleSetter title="Reports and Forms">
                <ReportsAndForms originalUrl="" title="Reports and Forms" />
              </TitleSetter>
            }
          />
        )}
        <Route path="actionregister/*" element = {<ActionRegister originalUrl="" title="Action Register"/>}/>
        <Route path="rmas/*" element = {<Rmas originalUrl="" title="Return Material Authorizations"/>}/>
        <Route path="followup/*" element = {<FollowUp originalUrl="" title="FollowUps"/>}/>
        <Route path="maintenance-log/*" element={<TitleSetter title="Maintenance Log"><MaintenanceLog originalUrl="" title="Maintenance Log" /></TitleSetter>} />
        <Route path="maintenance_log/*" element={<TitleSetter title="Maintenance Log"><MaintenanceLog originalUrl="" title="Maintenance Log" /></TitleSetter>} />
        <Route path="maintenancelog/*" element={<TitleSetter title="Maintenance Log"><MaintenanceLog originalUrl="" title="Maintenance Log" /></TitleSetter>} />
        <Route path="role-equipment/*" element={<TitleSetter title="Role Equipment"><RoleEquipment originalUrl="" title="Role Equipment" /></TitleSetter>} />
        <Route path="role_equipment/*" element={<TitleSetter title="Role Equipment"><RoleEquipment originalUrl="" title="Role Equipment" /></TitleSetter>} />
        <Route path="roleequipment/*" element={<TitleSetter title="Role Equipment"><RoleEquipment originalUrl="" title="Role Equipment" /></TitleSetter>} />
        <Route path="tech-log/*" element={<TitleSetter title="Tech Log"><TechLog originalUrl="" title="Tech Log" /></TitleSetter>} />
        <Route path="tech_log/*" element={<TitleSetter title="Tech Log"><TechLog originalUrl="" title="Tech Log" /></TitleSetter>} />
        <Route path="techlog/*" element={<TitleSetter title="Tech Log"><TechLog originalUrl="" title="Tech Log" /></TitleSetter>} />
        <Route path="trend/*" element={<TitleSetter title="Engine Trend"><Trend originalUrl="" title="Engine Trend" /></TitleSetter>} />
        <Route path="engine-trend/*" element={<TitleSetter title="Engine Trend"><Trend originalUrl="" title="Engine Trend" /></TitleSetter>} />
        <Route path="engine_trend/*" element={<TitleSetter title="Engine Trend"><Trend originalUrl="" title="Engine Trend" /></TitleSetter>} />
        <Route path="module-placeholder/Trend/*" element={<TitleSetter title="Engine Trend"><Trend originalUrl="" title="Engine Trend" /></TitleSetter>} />
        <Route path="xero/callback" element={<XeroCallback />} />
        <Route path="xero/*" element={<TitleSetter title="Xero"><XeroSettings /></TitleSetter>} />
        <Route path="finance/xero/*" element={<TitleSetter title="Xero"><XeroSettings /></TitleSetter>} />
        <Route path="transpond/*" element={<TitleSetter title="Transpond"><Transpond originalUrl="" title="Transpond" /></TitleSetter>} />
        <Route path="microsoft365/*" element={<TitleSetter title="Microsoft 365"><Microsoft365 /></TitleSetter>} />
        <Route path="sage300/*" element={<TitleSetter title="Sage 300"><Sage300 /></TitleSetter>} />
        <Route path="laserfiche/*" element={<TitleSetter title="Laserfiche"><Laserfiche /></TitleSetter>} />
        <Route path="landing/:sectionName/*" element={<SectionLanding />} />

        <Route path="dash/:id" element = {<MetaBaseDashboard />}/>
        <Route path="metabase/*" element={<TitleSetter title="Metabase Reporting"><MetabaseRoutes /></TitleSetter>} />
        <Route path="metabase-reporting/*" element={<TitleSetter title="Metabase Reporting"><MetabaseRoutes /></TitleSetter>} />
        <Route path="metabase_reports/*" element={<TitleSetter title="Metabase Reporting"><MetabaseRoutes /></TitleSetter>} />
        <Route path="admin/metabase/*" element={<TitleSetter title="Metabase Reporting"><MetabaseRoutes /></TitleSetter>} />
        <Route path="system-config/*" element={<TitleSetter title="System Config"><SystemConfig /></TitleSetter>} />
        <Route path="system_config/*" element={<TitleSetter title="System Config"><SystemConfig /></TitleSetter>} />
        <Route path="systemconfig/*" element={<TitleSetter title="System Config"><SystemConfig /></TitleSetter>} />
        <Route path="types/*" element={<TitleSetter title="System Config"><SystemConfig /></TitleSetter>} />
        <Route path="reports/*" element={<TitleSetter title="Reports"><SystemReports /></TitleSetter>} />
        <Route path="system-reports/*" element={<TitleSetter title="Reports"><SystemReports /></TitleSetter>} />
        <Route path="system_reports/*" element={<TitleSetter title="Reports"><SystemReports /></TitleSetter>} />
        <Route path="personnel/*" element={<TitleSetter title="Personnel"><Personnel originalUrl="" title="Personnel" /></TitleSetter>} />
        <Route path="history/*" element={<TitleSetter title="History"><History originalUrl="" title="History" /></TitleSetter>} />
        <Route path="tools/*" element={<TitleSetter title="Tools"><Tools originalUrl="" title="Tools" /></TitleSetter>} />
        <Route path="toolrequests/*" element={<TitleSetter title="Tool Requests"><ToolRequests originalUrl="" title="Tool Requests" /></TitleSetter>} />
        <Route path="tasks/*" element={<TitleSetter title="Tasks"><Tasks originalUrl="" title="Tasks" /></TitleSetter>} />
        {/* The Log Book register + entry record (Aircraft Patch 5). Static as well as nav-driven
            (`lbe`), because the aircraft and work order Log Book tabs open /lbe/:id whether or
            not the tenant's nav lists Log Book; the API owns who may write. */}
        <Route path="lbe/*" element={<TitleSetter title="Log Book"><AircraftServices originalUrl="" title="Log Book" /></TitleSetter>} />
        <Route
          path="manifest/*"
          element={
            <TitleSetter title="Pax Manifest">
              <Manifest originalUrl="" title="Pax Manifest" />
            </TitleSetter>
          }
        />
        <Route path="AddressBook/*" element={<TitleSetter title="Address Book"><Companies originalUrl="" title="Address Book" /></TitleSetter>} />
        <Route path="companies/*" element={<TitleSetter title="Address Book"><Companies originalUrl="" title="Address Book" /></TitleSetter>} />
        <Route path="supplier_invoices/*" element={<TitleSetter title="Supplier Invoices"><SupplierInvoices originalUrl="" title="Supplier Invoices" /></TitleSetter>} />
        <Route path="supplier-invoices/*" element={<TitleSetter title="Supplier Invoices"><SupplierInvoices originalUrl="" title="Supplier Invoices" /></TitleSetter>} />
        <Route path="customer_quotes/*" element={<TitleSetter title="Customer Quotes"><Quotes originalUrl="" title="Customer Quotes" /></TitleSetter>} />
        <Route path="customer-quotes/*" element={<TitleSetter title="Customer Quotes"><Quotes originalUrl="" title="Customer Quotes" /></TitleSetter>} />
        {nav && nav.length > 0 && <Route path="*" element={<NotFound />} />}
        {nav.map((navItem, index) =>
          navItem.children.map((menuItem, index) => {
            if (menuItem.to) {
              const featureName = menuItem.remote_menu_key
                ? menuItem.remote_menu_key
                : menuItem.function;
              const feature = getFeature(featureName);
              if (feature) {
                let FeatureComponent = feature.component;
                if (smallScreen && feature.responsiveComponent) {
                  FeatureComponent = feature.responsiveComponent;
                }

                return (
                  <Route
                    path={menuItem.to.split("?")[0] + "/*"}
                    element={
                      <TitleSetter title={menuItem.name}>
                        <FeatureComponent
                          originalUrl={menuItem.url}
                          title={menuItem.name}
                        />
                      </TitleSetter>
                    }
                  />
                );
              }
            }
            return null;
          })
        )}
      </Route>
    </SentryRoutes>
    </PageTitleProvider>
  );
};

export enum UsernameAttributes {
  EMAIL = "email",
  PHONE_NUMBER = "phone_number",
  USERNAME = "username",
}

const CustomTheme = {
  container: {
    height: "100%",
  },
};

const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  ({ theme }) => ({
    "&.notistack-MuiContent-success": {
      backgroundColor: theme.palette.primary.main,
    },
  })
);

const NotistackProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  // add action to all snackbars
  const notistackRef = createRef<any>();

  const onClickDismiss = (key) => () => {
    notistackRef.current!.closeSnackbar(key);
  };

  return (
    <SnackbarProvider
      maxSnack={10}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      preventDuplicate
      ref={notistackRef}
      action={(key) => (
        <Button onClick={onClickDismiss(key)} sx={{ color: "white" }}>
          Dismiss
        </Button>
      )}
      children={children}
      Components={{
        success: StyledMaterialDesignContent,
      }}
    />
  );
};

class AppWithAuth extends Component {
  componentDidMount() {
    hideLoader();
  }

  render() {
    const isLocalDev =
      window.location.hostname.includes("localhost") ||
      window.location.hostname.includes("127.0.0.1") ||
      process.env.REACT_APP_ENV === "local";

    const gate =
      getAuthProvider() === "gcip" ? (
        <GcipGate>
          <App authState="signedIn" />
        </GcipGate>
      ) : (
        /* @ts-ignore */
        <Authenticator
          authState="signIn"
          hide={[SignIn, Loading, Greetings]}
          usernameAttributes={UsernameAttributes.EMAIL}
          theme={CustomTheme}
        >
          <CustomSignIn />
          <CustomLoading />
          <App />
        </Authenticator>
      );

    return (
      <NotistackProvider>
        {isLocalDev ? <App authState="signedIn" /> : gate}
      </NotistackProvider>
    );
  }
}

export { App };
export default AppWithAuth;
